import VerifyAgeBanner from "@/components/VerifyAgeBanner";
import VerifyRequiredGate from "@/components/VerifyRequiredGate";
import { useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import LeftRail from "./LeftRail";
import RightRail from "./RightRail";
import ComposerModal from "./ComposerModal";
import GoLiveModal from "./GoLiveModal";
import BattleChallengeBanner from "./BattleChallengeBanner";
import VipAwardAnnouncer from "./vip/VipAwardAnnouncer";
import TopBadgeCongrats from "./TopBadgeCongrats";
import MobileNav from "./MobileNav";
import MobileHeader from "./MobileHeader";
import GoldenTicketModal from "./GoldenTicketModal";
import OnboardingModal from "./OnboardingModal";
import TermsAcceptanceGate from "./TermsAcceptanceGate";
import SidePickerModal from "./SidePickerModal";
// import PandaGuideBubble from "./PandaGuideBubble"; // temporarily removed per owner request — restore by uncommenting
import AdminControlCenter, { ShieldButton } from "./control-center/AdminControlCenter";
import { ControlCenterProvider } from "@/context/ControlCenterContext";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isGoldenTicketDay } from "@/lib/goldenTicket";

export default function AppShell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [composeOpen, setComposeOpen] = useState(false);
  const [goLiveOpen, setGoLiveOpen] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(false);
  const isSunday = isGoldenTicketDay();

  // Phase 2 — the moment POST /api/live returns, the host must land INSIDE
  // their own LiveRoom so LiveKit connects + camera/mic publish. Without
  // this, the stream row exists but no publisher track is bound and viewers
  // see a black screen until the host manually navigates in.
  const handleGoLiveStarted = (stream) => {
    // Fix A — carry the created stream in navigation state so LivePage opens
    // the host's room immediately (UI-only; publish rights come from the
    // server-issued LiveKit token, never from this object).
    if (stream?.id) navigate(`/live?s=${stream.id}`, { replace: false, state: { hostStream: stream } });
  };

  // Stable Outlet context so route pages don't re-render on unrelated shell state changes.
  const outletContext = useMemo(() => ({
    openCompose: () => setComposeOpen(true),
    openGoLive: () => setGoLiveOpen(true),
    openTicket: () => setTicketOpen(true),
  }), []);

  // Auto-prompt the daily Golden Ticket ONCE per session per calendar day —
  // but only on Sundays, per the Feb 2026 monetization rules.
  useEffect(() => {
    if (!user || !user.id) return;
    if (!isSunday) return;
    (async () => {
      try {
        const { data } = await api.get("/scratch/today");
        if (!data.available) return;
        const key = `pandax_ticket_prompted_${user.id}_${data.date_key}`;
        // SECURITY NOTE: sessionStorage flag = "we already showed the
        // scratcher modal this browser session for this user+date". Not
        // auth data. Auto-cleared when the tab closes.
        if (sessionStorage.getItem(key)) return;
        sessionStorage.setItem(key, "1");
        setTimeout(() => setTicketOpen(true), 900); // let the page paint first
      } catch { /* silent */ }
    })();
  }, [user, isSunday]);

  const loc = useLocation();
  const wide = loc.pathname.startsWith("/admin");
  // ?embed=1 → rendered inside the Live room's Admin drawer (iframe): no header/rails/nav.
  const embed = new URLSearchParams(loc.search).get("embed") === "1";

  return (
    <ControlCenterProvider>
    <div className="grain relative min-h-screen">
      {!embed && (
        <MobileHeader
          onCompose={() => setComposeOpen(true)}
          onGoLive={() => setGoLiveOpen(true)}
          onTicket={isSunday ? () => setTicketOpen(true) : null}
        />
      )}
      <div className={embed ? "" : "grid grid-cols-1 lg:grid-cols-12 gap-0 lg:gap-8 max-w-[1400px] mx-auto lg:px-6"} data-embed={embed ? "1" : undefined}>
        {!embed && (
          <LeftRail
            onCompose={() => setComposeOpen(true)}
            onGoLive={() => setGoLiveOpen(true)}
            onTicket={isSunday ? () => setTicketOpen(true) : null}
          />
        )}
        <main className={embed ? "min-h-screen pb-8" : `${wide ? "lg:col-span-9" : "lg:col-span-6"} min-h-screen border-x border-white/5 pb-24 lg:pb-8`}>
          {!embed && <VerifyAgeBanner />}
          <VerifyRequiredGate><Outlet context={outletContext} /></VerifyRequiredGate>
        </main>
        {!wide && !embed && <RightRail />}
      </div>
      {user && (
        <>
          <ComposerModal
            open={composeOpen}
            onOpenChange={setComposeOpen}
            onCreated={() => window.dispatchEvent(new Event("pandax:refresh-feed"))}
          />
          <GoLiveModal
            open={goLiveOpen}
            onOpenChange={setGoLiveOpen}
            onStarted={handleGoLiveStarted}
          />
          <BattleChallengeBanner />
          <VipAwardAnnouncer />
          <TopBadgeCongrats />
          {isSunday && <GoldenTicketModal open={ticketOpen} onOpenChange={setTicketOpen} />}
          <OnboardingModal />
          <TermsAcceptanceGate />
          <SidePickerModal open={!user.side} forceLocked />
          {/* <PandaGuideBubble /> — temporarily removed per owner request */}
          <AdminControlCenter />
          {!embed && <ShieldButton />}
        </>
      )}
      {!embed && <MobileNav onCompose={() => setComposeOpen(true)} onGoLive={() => setGoLiveOpen(true)} />}
    </div>
    </ControlCenterProvider>
  );
}
